import { useSelector } from "react-redux";
import { useRef, useState, useEffect } from "react";
import produce from "immer";
import * as echarts from "echarts";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { getBodyFont } from "common/utils/font";
import { IconViewed, IconBlueBird } from "features/user/assets";

enum ChartType {
  TOTAL_FAVORITED = "TOTAL_FAVORITED",
  NEW_FAVORITED = "NEW_FAVORITED",
  TOTAL_VIEWED = "TOTAL_VIEWED",
  AVG_TOTAL_FAVORITED = "AVG_TOTAL_FAVORITED",
}

const generateChartOption = (
  periodSeries: string[], // MM-DD
  totalFavoritedSeries: number[],
  totalViewedSeries: number[],
  newFavoritedSeries: number[],
  newFavoritedAvgValue?: number,
) => {
  return {
    animation: false,
    grid: {
      left: "3%",
      right: "4%",
      bottom: "120px",
    },
    xAxis: [
      {
        type: "category",
        boundaryGap: true,
        data: periodSeries,
        splitArea: {
          show: true,
          areaStyle: {
            color: ["rgba(148, 196, 247, 0)", "rgba(112, 177, 244, 0.15)"],
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          interval: 0,
          formatter: (value: string) => {
            const date = value.split("-");
            return `{month|${Number(date[0])}/}{day|${date[1]}}`;
          },
          rich: {
            month: {
              fontSize: 12,
              color: "#3F4A61",
              verticalAlign: "top",
              fontFamily: getBodyFont(),
            },
            day: {
              fontSize: 24,
              color: "#3F4A61",
              fontFamily: getBodyFont(),
            },
          },
          show: true,
          margin: 45,
          padding: [18, 0, 0, 0],
          width: 56,
          height: 32,
          backgroundColor: {
            image: `${Constants.assetHost}/assets/images/user_page_axis_label_bg.png`,
          },
        },
      },
    ],
    yAxis: [
      {
        show: false,
        max: function (value) {
          return value.max / 0.7;
        },
        axisTick: { show: false },
      },
      {
        show: false,
        max: function (value) {
          return value.max / 0.9;
        },
        axisTick: { show: false },
      },
      {
        show: false,
        max: function (value) {
          return value.max / 0.7;
        },
        axisTick: { show: false },
      },
      {
        show: false,
        max: function (value) {
          return value.max / 0.2;
        },
        axisTick: { show: false },
      },
    ],
    series: [
      {
        type: "bar",
        barWidth: "98%",
        itemStyle: { color: "#36c330" },
        data: totalFavoritedSeries,
      },
      {
        type: "line",
        data: totalViewedSeries,
        yAxisIndex: 1,
        lineStyle: { color: "white", width: 4, type: "dashed" },
        showSymbol: true,
        symbolSize: [32, 32],
        symbol: `image://${Constants.assetHost}/assets/images/user_page_chart_creator_viewed_icon.png`,
      },
      {
        type: "line",
        data: newFavoritedSeries,
        yAxisIndex: 2,
        lineStyle: { color: "#FEC1BF", width: 8 },
        label: {
          show: true,
          position: "inside",
          formatter: (params: object) =>
            params["dataIndex"] === newFavoritedSeries.length - 1
              ? `{latest|${params["value"]}}`
              : `{normal|${params["value"]}}`,
          rich: {
            latest: {
              fontSize: 32,
              color: "white",
              fontFamily: getBodyFont(),
            },
            normal: {
              fontSize: 24,
              color: "white",
              fontFamily: getBodyFont(),
            },
          },
        },
        labelLayout(params: object) {
          if (params["dataIndex"] === newFavoritedSeries.length - 1) {
            return {
              x: params["labelRect"].x,
              y: params["rect"].y + params["rect"].height / 2 - 6,
            };
          } else {
            return {
              x: params["labelRect"].x,
              y: params["labelRect"].y - 3,
            };
          }
        },
        showSymbol: true,
        symbolSize: (value: number, params: object) => {
          if (params["dataIndex"] === newFavoritedSeries.length - 1) {
            return [100, 100];
          } else {
            return [56, 48];
          }
        },
        symbol: (value: number, params: object) => {
          if (params["dataIndex"] === newFavoritedSeries.length - 1) {
            return `image://${Constants.assetHost}/assets/images/user_page_creator_favorited_icon_latest.png`;
          } else {
            return `image://${Constants.assetHost}/assets/images/user_page_creator_favorited_icon.png`;
          }
        },
      },
      {
        type: "line",
        yAxisIndex: 3,
        showSymbol: false,
        lineStyle: { opacity: 0 },
        markLine: {
          symbol: "none",
          label: { show: false },
          data: [{ type: "max" }],
          lineStyle: {
            width: 12,
            cap: "round",
            type: [0, 20],
            color: "#F9E532",
          },
        },
        data: periodSeries.map(() => newFavoritedAvgValue),
      },
    ],
  };
};

export const UserProgressCreatorChart = ({ selected }: { selected: boolean }) => {
  const play = usePlaySound();
  const ref = useRef<HTMLDivElement>(null);
  const [chartTypes, setChartTypes] = useState<ChartType[]>([
    ChartType.TOTAL_FAVORITED,
    ChartType.NEW_FAVORITED,
    ChartType.TOTAL_VIEWED,
  ]);
  const [chart, _setChart] = useState<echarts.ECharts | null>(null);
  const chartRef = useRef(chart);
  const setChart = (chart: echarts.ECharts) => {
    _setChart(chart);
    chartRef.current = chart;
  };
  const status = useSelector((state: RootState) => state.user.status);
  const history = useSelector((state: RootState) => state.user.history);

  useEffect(() => {
    if (chartRef.current) {
      const option = generateChartOption(
        history.period,
        chartTypes.includes(ChartType.TOTAL_FAVORITED)
          ? history.project.favorited.total.day
              .map((value) => value.favorited)
              .concat([status.project.favorited])
          : [],
        chartTypes.includes(ChartType.TOTAL_VIEWED)
          ? history.project.viewed.total.day
              .map((value) => value.viewed)
              .concat([status.project.viewed])
          : [],
        chartTypes.includes(ChartType.NEW_FAVORITED)
          ? history.project.favorited.new.day
              .map((value) => value.favorited)
              .concat([
                status.project.favorited -
                  history.project.favorited.total.day[
                    history.project.favorited.total.day.length - 1
                  ].favorited,
              ])
          : [],
      );
      chartRef.current.setOption(option);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartTypes.length]);

  useEffect(() => {
    if (ref.current && history && !chartRef.current && selected) {
      const chart = echarts.init(ref.current, null, { renderer: "svg" });
      const option = generateChartOption(
        history.period,
        chartTypes.includes(ChartType.TOTAL_FAVORITED)
          ? history.project.favorited.total.day
              .map((value) => value.favorited)
              .concat([status.project.favorited])
          : [],
        chartTypes.includes(ChartType.TOTAL_VIEWED)
          ? history.project.viewed.total.day
              .map((value) => value.viewed)
              .concat([status.project.viewed])
          : [],
        chartTypes.includes(ChartType.NEW_FAVORITED)
          ? history.project.favorited.new.day
              .map((value) => value.favorited)
              .concat([
                status.project.favorited -
                  history.project.favorited.total.day[
                    history.project.favorited.total.day.length - 1
                  ].favorited,
              ])
          : [],
      );
      chart.setOption(option);
      setChart(chart);
    }
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
      }
      setChart(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, history, selected]);

  const handleSelectChartType = (chartType: ChartType) => () => {
    play();
    setChartTypes(
      produce((draft) => {
        const targetIndex = draft.findIndex((type) => type === chartType);
        if (targetIndex !== -1) {
          draft.splice(targetIndex, 1);
        } else {
          draft.push(chartType);
        }
      }),
    );
  };

  if (!history || !status) {
    return (
      <div
        className="flex-col-el flex-1 w-full h-full items-start justify-start overflow-hidden"
        style={{ display: selected ? undefined : "none" }}
      >
        <div className="flex-row-el flex-center relative w-full h-full overflow-hidden">
          <img
            alt="ゲームの背景"
            src={`${Constants.assetHost}/assets/images/bg_user_page_history.png`}
            className="w-full h-full absolute top-0 left-0"
          />
          <div className="flex-col-center w-[100px] h-[100px]">
            <div className="dots-loader" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex-col-el flex-1 w-full h-full items-start justify-start overflow-hidden"
      style={{ display: selected ? undefined : "none" }}
    >
      <div className="flex-row-el flex-center relative w-full h-full overflow-hidden">
        <img
          alt="ゲームの背景"
          src={`${Constants.assetHost}/assets/images/bg_user_page_history.png`}
          className="w-full h-full absolute top-0 left-0"
        />

        <div className="flex-row-el flex-center w-[720px] h-[600px] z-10">
          <div ref={ref} className="flex-row-el w-full h-full" />
        </div>

        <div className="flex-col-el flex-center absolute right-4 top-1/2 -translate-y-1/2 gap-4 w-[104px] z-10">
          <div
            className="flex-col-el flex-center relative cursor-pointer transition mt-4"
            style={{
              transform: `translateY(${chartTypes.includes(ChartType.TOTAL_VIEWED) ? 3 : 0}px)`,
            }}
            onClick={handleSelectChartType(ChartType.TOTAL_VIEWED)}
          >
            <div className="flex-col-el z-10 w-[56px] h-[56px]">
              <IconViewed />
            </div>
            <div
              className={`flex-col-el flex-center rounded-full w-[96px] h-[96px] -mt-4 pt-2 transition ${
                chartTypes.includes(ChartType.TOTAL_VIEWED)
                  ? "text-white bg-brown-80"
                  : "text-textcolor-black bg-white"
              }`}
              style={{
                boxShadow: chartTypes.includes(ChartType.TOTAL_VIEWED)
                  ? "0px 3px 3px -3px rgba(0, 0, 0, 0.2), 0px 3px 4px 1px rgba(0, 0, 0, 0.14), 0px 1px 8px 3px rgba(0, 0, 0, 0.12)"
                  : "0px 6px 6px -3px rgba(0, 0, 0, 0.2), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)",
              }}
            >
              <p className="text-body whitespace-pre text-[16px] leading-5">
                {I18n.t("MSG_USER_PROFILE_PROGRESS_CREATOR_CHART_VIEW_TITLE_START")}
              </p>
              <p className="text-body whitespace-pre text-[32px] leading-8">
                {status.project.viewed}
              </p>
              <p className="text-body whitespace-pre text-[16px] leading-5">
                {I18n.t("MSG_USER_PROFILE_PROGRESS_CREATOR_CHART_VIEW_TITLE_END")}
              </p>
            </div>
          </div>

          <div
            className="flex-col-el flex-center relative cursor-pointer transition"
            style={{
              transform: `translateY(${chartTypes.includes(ChartType.TOTAL_FAVORITED) ? 3 : 0}px)`,
            }}
            onClick={handleSelectChartType(ChartType.TOTAL_FAVORITED)}
          >
            <div className="flex-col-el z-10">
              <img
                alt="*"
                src={`${Constants.assetHost}/assets/images/user_page_creator_favorited_icon.png`}
              />
            </div>
            <div
              className={`flex-col-el flex-center rounded-full w-[96px] h-[96px] -mt-3 pt-2 transition ${
                chartTypes.includes(ChartType.TOTAL_FAVORITED)
                  ? "text-white bg-red-100"
                  : "text-textcolor-black bg-white"
              }`}
              style={{
                boxShadow: chartTypes.includes(ChartType.TOTAL_FAVORITED)
                  ? "0px 3px 3px -3px rgba(0, 0, 0, 0.2), 0px 3px 4px 1px rgba(0, 0, 0, 0.14), 0px 1px 8px 3px rgba(0, 0, 0, 0.12)"
                  : "0px 6px 6px -3px rgba(0, 0, 0, 0.2), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)",
              }}
            >
              <p className="text-body whitespace-pre text-[16px] leading-5">
                {I18n.t("MSG_USER_PROFILE_PROGRESS_CREATOR_CHART_FAVORITED_TITLE_START")}
              </p>
              <p className="text-body whitespace-pre text-[32px] leading-8">
                {status.project.favorited}
              </p>
              <p className="text-body whitespace-pre text-[16px] leading-5">
                {I18n.t("MSG_USER_PROFILE_PROGRESS_CREATOR_CHART_FAVORITED_TITLE_END")}
              </p>
            </div>
          </div>

          <div
            className="flex-col-el flex-center relative cursor-pointer transition"
            style={{
              transform: `translateY(${chartTypes.includes(ChartType.NEW_FAVORITED) ? 3 : 0}px)`,
            }}
            onClick={handleSelectChartType(ChartType.NEW_FAVORITED)}
          >
            <div className="flex-col-el z-10">
              <IconBlueBird />
            </div>
            <div
              className={`flex-col-el flex-center rounded-full w-[96px] h-[96px] -mt-3 pt-2 transition ${
                chartTypes.includes(ChartType.NEW_FAVORITED)
                  ? "text-white bg-red-100"
                  : "text-textcolor-black bg-white"
              }`}
              style={{
                boxShadow: chartTypes.includes(ChartType.NEW_FAVORITED)
                  ? "0px 3px 3px -3px rgba(0, 0, 0, 0.2), 0px 3px 4px 1px rgba(0, 0, 0, 0.14), 0px 1px 8px 3px rgba(0, 0, 0, 0.12)"
                  : "0px 6px 6px -3px rgba(0, 0, 0, 0.2), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)",
              }}
            >
              <p className="text-body whitespace-pre text-[16px] leading-5">
                {I18n.t("MSG_USER_PROFILE_PROGRESS_NEW_TITLE")}
              </p>
              <p className="text-body whitespace-pre text-[32px] leading-8">
                {status.project.favorited -
                  history.project.favorited.total.day[
                    history.project.favorited.total.day.length - 1
                  ].favorited}
              </p>
              <p className="text-body whitespace-pre text-[16px] leading-5">
                {I18n.t("MSG_USER_PROFILE_PROGRESS_CREATOR_CHART_FAVORITED_TITLE_END")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
